import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import { isUndefined, isNaN } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Select, Input, Combobox, Autocomplete } from '~/components/Form';
import {
  DESCARGA_NF,
  SOBRAS_E_FALTAS,
  TELEMETRIA,
  CUSTOMIZACAO_RAIZEN,
} from '~/constants/modules';
import { t, can, validCharacters } from '~/helpers';
import api from '~/services/api';
import {
  storeTanqueFisico,
  updateTanqueFisico,
} from '~/store/modules/tanquefisico/actions';

import InputMaxLength from '../componentes/InputMaxLength';

const TIPO_SONDA_STOCK300 = 18;

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleLoading,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.tanquefisico);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const loadingErrorMessage = 's.geral.loadingerror';

  const initials = create ? data : { ...find, ...data };

  const [capacidade, setCapacidade] = useState(
    Number(initials.capacidadetanquefisico)
  );
  const [percoper, setPercoper] = useState(
    Number(initials.capacidadeoperacionaltanquefisico)
  );

  const user = useSelector((state) => state.user);

  const volumeOperacional = React.useMemo(() => {
    if (percoper >= 100) return capacidade;
    return Math.abs(Number((capacidade * percoper) / 100));
  }, [capacidade, percoper]);

  const [posto, setPosto] = useState(0);
  const [tipoPosto, setTipoPosto] = useState('C');
  const [tipoSonda, setTipoSonda] = useState(0);
  const isStock300 = Number(tipoSonda) === TIPO_SONDA_STOCK300;
  const [tanqueTV, setTanqueTV] = useState(0);
  const [
    tipoFechamentoViagemComboio,
    setTipoFechamentoViagemComboio,
  ] = useState(4);

  const handleAsyncLoadingChange = useCallback((isLoading) => {
    setPendingRequests((state) => {
      if (hasLoadingError) return 0;

      if (isLoading) return state + 1;

      return state > 0 ? state - 1 : 0;
    });
  }, [hasLoadingError]);

  const handleAsyncLoadingError = useCallback(() => {
    setHasLoadingError(true);
    setPendingRequests(0);
  }, []);

  const handleGetPosto = React.useCallback((id, type) => {
    setPosto(id);
    setTipoPosto(type);
  }, []);

  const handleGetComboio = React.useCallback(async (id) => {
    try {
      handleAsyncLoadingChange(true);
      await api
        .get('comboios', {
          params: {
            wherefield: 'idposto',
            valuewherefield: id,
            typewherefield: 'EQUALS',
          },
        })
        .then(({ data }) => {
          const item = Number(data.items[0].tipofechamentoviagemcomboio);
          setTipoFechamentoViagemComboio([2, 3, 4].includes(item));
        });
    } catch (err) {
      handleAsyncLoadingError();
    } finally {
      handleAsyncLoadingChange(false);
    }
  }, [handleAsyncLoadingChange, handleAsyncLoadingError]);

  async function handleSubmit(param) {
    reference.current.setErrors({});

    try {
      if (hasLoadingError) {
        toast.error(t(loadingErrorMessage));
        return;
      }

      if (pendingRequests > 0) {
        toast.warning(t('s.geral.validacao.aguardecarregamento'));
        return;
      }

      const schema = Yup.object().shape({
        idposto: Yup.number(t('s.geral.validacao.informeumnumero'))
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        codigotanquefisico: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          )
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        codigoauxiliartanquefisico: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          )
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        codigocosantanquefisico: can(CUSTOMIZACAO_RAIZEN)
          ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.informeumnumero'))
              .min(0, t('s.geral.validacao.valorminimo'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        capacidadetanquefisico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          )
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        capacidadeoperacionaltanquefisico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.informeumnumero'))
          .min(0, t('s.geral.validacao.valorminimo'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        tipotanquefisico: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        exibeestoquetanquefisico:
          tipoSonda === 0
            ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
        statustanquefisico: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        idtanquevirtual:
          can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)
            ? Yup.string()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string(),
        idarqueacao:
          (((can(TELEMETRIA) || can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)) &&
            tipoPosto === 'P') ||
            (tipoPosto === 'C' && tipoFechamentoViagemComboio))
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        idproduto: !(can(DESCARGA_NF) || can(SOBRAS_E_FALTAS))
          ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.number(),
        tiposondatanquefisico: can(TELEMETRIA)
          ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.number(),
        limiaraguatanquefisico: can(TELEMETRIA)
          ? Yup.number()
              .transform((value) => parseFloat(value))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          : Yup.number(),
        limiarprodutotanquefisico: can(TELEMETRIA)
          ? Yup.number()
              .transform((value) => parseFloat(value))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          : Yup.number(),
        numerosondatanquefisico:
          can(TELEMETRIA) && tipoSonda > 0 && !isStock300
            ? Yup.number()
                .nullable()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number().nullable(),
        enderecosondatanquefisico:
          can(TELEMETRIA) && tipoSonda > 0
            ? Yup.string()
                .nullable()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
        portasocketsondatanquefisico:
          can(TELEMETRIA) && tipoSonda > 0
            ? Yup.number()
                .nullable()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number().nullable(),
        deltatanquefisico:
          can(TELEMETRIA) && tipoSonda > 0
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .transform((value) => (isNaN(value) ? undefined : value))
                .integer(t('s.geral.validacao.deveserinteiro'))
                .moreThan(
                  -5001,
                  t('s.geral.validacao.valorminimo').replace('%s', -5000)
                )
                .lessThan(
                  5001,
                  t('s.geral.validacao.valormaximo').replace('%s', 5000)
                )
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        deltaaguatanquefisico:
          can(TELEMETRIA) && tipoSonda > 0
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .transform((value) => (isNaN(value) ? undefined : value))
                .integer(t('s.geral.validacao.deveserinteiro'))
                .moreThan(
                  -5001,
                  t('s.geral.validacao.valorminimo').replace('%s', -5000)
                )
                .lessThan(
                  5001,
                  t('s.geral.validacao.valormaximo').replace('%s', 5000)
                )
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        alarmenivelmuitobaixotanquefisico:
          can(TELEMETRIA) && tipoSonda > 0
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .transform((value) => (isNaN(value) ? undefined : value))
                .integer(t('s.geral.validacao.deveserinteiro'))
                .moreThan(
                  -1,
                  t('s.geral.validacao.valorminimo').replace('%s', 0)
                )
                .lessThan(
                  101,
                  t('s.geral.validacao.valormaximo').replace('%s', 100)
                )
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        alarmenivelbaixotanquefisico:
          can(TELEMETRIA) && tipoSonda > 0
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .transform((value) => (isNaN(value) ? undefined : value))
                .integer(t('s.geral.validacao.deveserinteiro'))
                .moreThan(
                  -1,
                  t('s.geral.validacao.valorminimo').replace('%s', 0)
                )
                .lessThan(
                  101,
                  t('s.geral.validacao.valormaximo').replace('%s', 100)
                )
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        alarmenivelaltotanquefisico:
          can(TELEMETRIA) && tipoSonda > 0
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .transform((value) => (isNaN(value) ? undefined : value))
                .integer(t('s.geral.validacao.deveserinteiro'))
                .moreThan(
                  -1,
                  t('s.geral.validacao.valorminimo').replace('%s', 0)
                )
                .lessThan(
                  101,
                  t('s.geral.validacao.valormaximo').replace('%s', 100)
                )
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),

        alarmenivelmuitoaltotanquefisico:
          can(TELEMETRIA) && tipoSonda > 0
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .transform((value) => (isNaN(value) ? undefined : value))
                .integer(t('s.geral.validacao.deveserinteiro'))
                .moreThan(
                  -1,
                  t('s.geral.validacao.valorminimo').replace('%s', 0)
                )
                .lessThan(
                  101,
                  t('s.geral.validacao.valormaximo').replace('%s', 100)
                )
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        variacaonivelturnofechadotanquefisico:
          can(TELEMETRIA) && tipoSonda > 0
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .transform((value) => (isNaN(value) ? undefined : value))
                .integer(t('s.geral.validacao.deveserinteiro'))
                .moreThan(
                  -1,
                  t('s.geral.validacao.valorminimo').replace('%s', 0)
                )
                .lessThan(
                  101,
                  t('s.geral.validacao.valormaximo').replace('%s', 100)
                )
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
        idposto: posto,
        tiposondatanquefisico: tipoSonda,
        numerosondatanquefisico: isStock300
          ? 1
          : param.numerosondatanquefisico,
        idarqueacao: param.idarqueacao,
      };

      if (create) {
        await dispatch(storeTanqueFisico(objSend, params));
      } else {
        await dispatch(updateTanqueFisico(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    const { idposto, tiposondatanquefisico, idtanquevirtual } = initials;

    if (!isUndefined(idposto)) {
      setPosto(idposto);
    }

    if (!isUndefined(idtanquevirtual)) {
      setTanqueTV(idtanquevirtual);
    }

    if (!isUndefined(tiposondatanquefisico) && tiposondatanquefisico >= 0) {
      setTipoSonda(tiposondatanquefisico);
    }
  }, [find]);

  useEffect(() => {
    toggleLoading(pendingRequests > 0);
  }, [pendingRequests, toggleLoading]);

  useEffect(() => {
    toggleConfirmButton(!hasLoadingError);
  }, [hasLoadingError, toggleConfirmButton]);

  useEffect(
    () => () => {
      toggleLoading(false);
      toggleConfirmButton(true);
    },
    [toggleLoading, toggleConfirmButton]
  );

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : hasLoadingError ? (
        <div
          style={{
            border: '1px solid #f5c6cb',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            padding: '16px',
            lineHeight: 1.5,
          }}
        >
          {t(loadingErrorMessage)}
        </div>
      ) : (
        <div style={{ position: 'relative', minHeight: '14rem' }}>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Autocomplete
              name="idposto"
              label="codigonomeposto"
              url="postoscomboios"
              unique="idposto"
              title={t('s.geral.postocomboio')}
              disabled={!create}
              getValue={({ idposto, tipoposto }) => {
                handleGetPosto(idposto, tipoposto);
                if (tipoposto) {
                  if (tipoposto.toUpperCase() === 'C') {
                    handleGetComboio(idposto);
                  }
                }
              }}
              onLoadingChange={handleAsyncLoadingChange}
              onLoadingError={handleAsyncLoadingError}
            />

          <Input
            name="codigotanquefisico"
            label={t('s.configuracao.tanquefisico.codigotanquefisico')}
            disabled={!create}
          />

          {user.configs.codauxtfnumerico == 'N' && (
            <Input
              name="codigoauxiliartanquefisico"
              label={t(
                's.configuracao.tanquefisico.codigoauxiliartanquefisico'
              )}
            />
          )}

          {user.configs.codauxtfnumerico == 'S' && (
            <Input
              name="codigoauxiliartanquefisico"
              type="number"
              label={t(
                's.configuracao.tanquefisico.codigoauxiliartanquefisico'
              )}
            />
          )}

          {can(CUSTOMIZACAO_RAIZEN) && (
            <Input
              name="codigocosantanquefisico"
              type="number"
              label={t('s.configuracao.tanquefisico.codigocosantanquefisico')}
            />
          )}

            {(can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)) && (
              <Combobox
                name="idtanquevirtual"
                label="tanquevirtualprodutotanquevirtual"
                url="tanquesvirtuais"
                unique="idtanquevirtual"
                title={t('s.configuracao.tanquefisico.idtanquevirtual')}
                // disabled={!posto || !create}
                // disabled={create ? !posto : !!tanqueTV}
                params={{
                  wherefield: 'idposto',
                  valuewherefield: initials.idposto || posto,
                  typewherefield: 'EQUALS',
                }}
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

            {(((can(TELEMETRIA) ||
              can(DESCARGA_NF) ||
              can(SOBRAS_E_FALTAS)) &&
              tipoPosto === 'P') ||
              (tipoPosto === 'C' && tipoFechamentoViagemComboio)) && (
              <Combobox
                name="idarqueacao"
                label="nomearqueacao"
                url="arqueacoes"
                unique="idarqueacao"
                title={t('s.configuracao.tanquefisico.idarqueacao')}
                disabled={!posto}
                params={{
                  wherefield: 'idposto',
                  valuewherefield: initials.idposto || posto,
                  typewherefield: 'EQUALS',
                  fieldlist: 'idarqueacao, nomearqueacao',
                }}
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

            {!(can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)) && (
              <Autocomplete
                name="idproduto"
                label="nomeproduto"
                url="produtos"
                unique="idproduto"
                title={t('s.configuracao.tanquefisico.idproduto')}
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

          <Input
            name="capacidadetanquefisico"
            label={t('s.configuracao.tanquefisico.capacidadetanquefisico')}
            type="number"
            getValue={(value) => setCapacidade(Number(value))}
          />

          {/* <Input
            name="capacidadeoperacionaltanquefisico"
            label={t('s.configuracao.tanquefisico.capacidadeoperacionaltanquefisico')}
            // type="number"
            InputProps={{ inputProps: { max: 3 } }}
            getValue={(value) => setPercoper(Number(value))}
          /> */}

          <InputMaxLength
            name="capacidadeoperacionaltanquefisico"
            label={t(
              's.configuracao.tanquefisico.capacidadeoperacionaltanquefisico'
            )}
            type="number"
            // InputProps={{ inputProps: { max: 3 } }}
            maxLength={3}
            maxValue={100}
            getValue={(value) => setPercoper(Number(value))}
          />

          <Input
            disabled
            name="volumeperacionaltanquefisico"
            label={t(
              's.configuracao.tanquefisico.volumeperacionaltanquefisico'
            )}
            value={volumeOperacional}
            style={{ backgroundColor: '#FFFFE0' }}
            type="number"
          />

            {can(TELEMETRIA) && (
              <Autocomplete
                name="tiposondatanquefisico"
                label="nomedriversonda"
                url="driverstanques"
                unique="iddriversonda"
                title={t('s.configuracao.tanquefisico.tiposondatanquefisico')}
                getValue={(value) => setTipoSonda(value?.iddriversonda)}
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

          {tipoSonda === 0 && (
            <Select
              name="exibeestoquetanquefisico"
              variant="outlined"
              size="small"
              label={t('s.geral.camposistema.exibeestoquetanquefisico')}
              options={[
                {
                  value: 'S',
                  label: t('s.geral.opcoes.sim'),
                },
                {
                  value: 'N',
                  label: t('s.geral.opcoes.nao'),
                },
              ]}
            />
          )}

          {can(TELEMETRIA) && tipoSonda > 0 && (
            <>
              {!isStock300 && (
                <Input
                  name="numerosondatanquefisico"
                  label={t(
                    's.configuracao.tanquefisico.numerosondatanquefisico'
                  )}
                  type="number"
                />
              )}
              <Input
                name="enderecosondatanquefisico"
                label={t(
                  's.configuracao.tanquefisico.enderecosondatanquefisico'
                )}
              />
              <Input
                name="portasocketsondatanquefisico"
                label={t(
                  's.configuracao.tanquefisico.portasocketsondatanquefisico'
                )}
                type="number"
              />
              <Input
                name="deltatanquefisico"
                label={t('s.configuracao.tanquefisico.deltatanquefisico')}
              />
              <Input
                name="deltaaguatanquefisico"
                label={t('s.configuracao.tanquefisico.deltaaguatanquefisico')}
              />
            </>
          )}

          {can(TELEMETRIA) && (
            <>
              <Input
                name="limiaraguatanquefisico"
                label={t('s.configuracao.tanquefisico.limiaraguatanquefisico')}
                type="number"
              />

              <Input
                name="limiarprodutotanquefisico"
                label={t(
                  's.configuracao.tanquefisico.limiarprodutotanquefisico'
                )}
                type="number"
              />
            </>
          )}

          {can(TELEMETRIA) && tipoSonda > 0 && (
            <>
              <Input
                name="alarmenivelmuitobaixotanquefisico"
                label={t(
                  's.configuracao.tanquefisico.alarmenivelmuitobaixotanquefisico'
                )}
                type="number"
              />
              <Input
                name="alarmenivelbaixotanquefisico"
                label={t(
                  's.configuracao.tanquefisico.alarmenivelbaixotanquefisico'
                )}
                type="number"
              />
              <Input
                name="alarmenivelaltotanquefisico"
                label={t(
                  's.configuracao.tanquefisico.alarmenivelaltotanquefisico'
                )}
                type="number"
              />
              <Input
                name="alarmenivelmuitoaltotanquefisico"
                label={t(
                  's.configuracao.tanquefisico.alarmenivelmuitoaltotanquefisico'
                )}
                type="number"
              />
              <Input
                name="variacaonivelturnofechadotanquefisico"
                label={t(
                  's.configuracao.tanquefisico.variacaonivelturnofechadotanquefisico'
                )}
                type="number"
              />
            </>
          )}

          <Select
            name="tipotanquefisico"
            variant="outlined"
            size="small"
            label={t('s.configuracao.tanquefisico.tipotanquefisico')}
            options={[
              {
                value: 'H',
                label: t('s.configuracao.tanquefisico.horizontal'),
              },
              { value: 'V', label: t('s.configuracao.tanquefisico.vertical') },
            ]}
          />

            <Select
              name="statustanquefisico"
              variant="outlined"
              size="small"
              label={t('s.configuracao.tanquefisico.statustanquefisico')}
              options={[
                { value: 'L', label: t('s.geral.opcoes.liberado') },
                { value: 'B', label: t('s.geral.opcoes.bloqueado') },
                { value: 'C', label: t('s.geral.opcoes.cancelado') },
              ]}
            />
          </Form>
          <Spinner loading={pendingRequests > 0} />
        </div>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func,
  toggleConfirmButton: PropTypes.func,
};

CreateOrUpdate.defaultProps = {
  data: {},
  toggleLoading: () => {},
  toggleConfirmButton: () => {},
};
